// 9fbef606107a605d69c0edbcd8029e5d
import logger from '@tcp/core/src/utils/loggerInstance';
import { API_CONFIG } from '@tcp/core/src/services/config';
import { generateSessionId } from '../../utils/cookie.util';
import { graphQLClient, statefulGraphQLClient } from '../api.constants';
import QueryBuilder from './graphQL/queries/queryBuilder';
import { importGraphQLClientDynamically, getAPIConfig, isServer } from '../../utils';
import StatefulAPIOldClient from './stateful/statefulClient';

import UnbxdAPIClient from './unbxd/unbxdClient';
import ExternalAPIClient from './external/externalClient';

import StatefulAPIClient from './stateful/statefulClientSFCC';
import { getStoreRef } from '../../utils/store.utils';
import { toggleSiteMaintenanceBanner } from '../../reduxStore/actions';

const TCP_TRACE_REQUEST_ID = 'tcp-trace-request-id';
const TCP_TRACE_SESSION_ID = 'tcp-trace-session-id';
const NO_TRACE_ID = 'NO-TRACE-ID';

/**
 * Logs error
 * @param {*} e error object
 */
const errorHandler = (
  {
    err,
    reqObj = {
      webService: {
        URI: 'GRAPHQL QUERY || URI_NOT_SENT_IN_ERROR_LOGGING',
      },
    },
    reqHeaders = {
      TCP_TRACE_REQUEST_ID: NO_TRACE_ID,
      TCP_TRACE_SESSION_ID: NO_TRACE_ID,
    },
  } = {},
  isExternalCall
) => {
  const { errorCode } = err || {};
  if (errorCode === 'MAINTENANCE_ERROR') {
    const storeRef = getStoreRef();
    storeRef.dispatch(toggleSiteMaintenanceBanner(true));
  }
  // unbxd-request-id
  const { sessionCookieKey } = API_CONFIG;
  let unbxdReqId = 'N/A';
  const sessionId = generateSessionId();
  if (err && err.response && err.response.headers) {
    unbxdReqId = err.response.headers['unbxd-request-id'];
  }
  const uri = reqObj.webService.URI || '';
  const uriWithoutQuery = isExternalCall ? 'External API failed' : uri.split('?')[0];

  logger.error({
    error: `Error in API Handler-${uriWithoutQuery}`,
    errorTags: [`API Handler-${uriWithoutQuery}`, sessionId],
    extraData: {
      ...reqObj,
      errorMessage: JSON.stringify(err),
      'trace-request-id': reqHeaders[TCP_TRACE_REQUEST_ID],
      'trace-session-id': reqHeaders[TCP_TRACE_SESSION_ID],
      'unbxd-request-id': unbxdReqId,
      [sessionCookieKey]: generateSessionId(true),
    },
  });
  throw err;
};

/**
 * Executes query on GraphQL Client
 * @param {*} query
 * @param {*} graphQLInterface
 */
const executeQuery = async ({
  query,
  fetchPolicy,
  graphQLInterface,
  cacheTag,
  isContextual,
  apiConfig,
  variables,
  type,
  updateQuery = false,
  cacheConfigKey,
  TTL,
  context,
  header,
  callbackParams,
  pageName,
}) => {
  const { sessionCookieKey } = API_CONFIG;
  try {
    let client = graphQLInterface();
    if (client instanceof Promise) {
      client = await client;
    }

    return client.executeQuery(
      query,
      apiConfig,
      {
        fetchPolicy,
        cacheTag,
        isContextual,
        variables,
        context,
        header,
        callbackParams,
        pageName,
      },
      type,
      updateQuery,
      cacheConfigKey,
      TTL
    );
  } catch (err) {
    logger.error(`Execute Query - ${cacheTag}`, {
      error: err,
      errorTags: [`Execute Query - ${cacheTag}`],
      extraData: {
        [sessionCookieKey]: generateSessionId(true),
      },
    });
    throw err;
  }
};

/**
 * Loads Stateful GraphQL client interface
 */
export const loadStatefulGraphQLInterface = () => {
  return importGraphQLClientDynamically(statefulGraphQLClient).then(
    ({ default: graphQLInterface }) => graphQLInterface
  );
};

/**
 * Executes a stateful graph ql query
 * @param {*} QueryBuilder GraphQL query builder which returns query wrapped with graphql-tag, passed in form of a default export
 * @returns {Promise} Resolves with data or rejects with error object
 */
export const executeStatefulGraphQLQuery = ({
  query,
  fetchPolicy,
  cacheTag,
  isContextual,
  apiConfig,
  variables,
  type,
  updateQuery,
  cacheConfigKey,
  TTL,
  context = {},
  header = {},
  callbackParams = {},
}) => {
  const config = apiConfig || getAPIConfig();
  return loadStatefulGraphQLInterface()
    .then((client) =>
      executeQuery({
        query,
        graphQLInterface: client,
        fetchPolicy,
        cacheTag,
        isContextual,
        apiConfig: config,
        variables,
        type,
        updateQuery,
        cacheConfigKey,
        TTL,
        context,
        header,
        callbackParams,
      })
    )
    .catch((err) => {
      throw err;
    });
};

/**
 * Loads GraphQL client interface
 */
const loadGraphQLInterface = () => {
  return importGraphQLClientDynamically(graphQLClient).then(
    ({ default: graphQLInterface }) => graphQLInterface
  );
};

/**
 * Executes a graph ql query
 * @param {*} QueryBuilder GraphQL query builder which returns query wrapped with graphql-tag, passed in form of a default export
 * @returns {Promise} Resolves with data or rejects with error object
 */
export const executeGraphQLQuery = ({
  query,
  fetchPolicy,
  cacheTag,
  isContextual,
  pageName,
  apiConfig: config,
}) => {
  let apiConfig = config;

  if (typeof config === 'undefined' || Object.keys(config).indexOf('brandId') < 0) {
    if (isServer()) {
      // send the log message only if server side error condition
      logger.error({
        error: `apiConfig is undefined or contains bad data for cacheTag: ${cacheTag}`,
        errorTags: ['executeGraphQLQuery', 'apiConfig', cacheTag],
        extraData: {
          cacheTag,
        },
      });
    }
    apiConfig = getAPIConfig();
  }

  let client;
  return loadGraphQLInterface()
    .then((loadedClient) => {
      client = loadedClient;
      return executeQuery({
        query,
        graphQLInterface: client,
        fetchPolicy,
        cacheTag,
        isContextual,
        apiConfig,
        pageName,
      });
    })
    .then((result) => {
      return result;
    })
    .catch((err) => {
      const apolloClient = client();
      const reqObj = {
        webService: {
          URI: 'GRAPHQL QUERY',
          query,
        },
      };
      const reqHeaders = {
        TCP_TRACE_REQUEST_ID: apolloClient?.traceId || NO_TRACE_ID,
        TCP_TRACE_SESSION_ID: apolloClient?.sessionId || NO_TRACE_ID,
      };
      errorHandler({
        err,
        reqObj,
        reqHeaders,
      });
    });
};

/**
 * Fetches Queries based on passed module, then executes the query and returns a promise for query execution
 * @param {*} moduleName Module for which query needs to be executed
 * @returns {Promise} Resolves with data or rejects with error object
 */
export const fetchModuleDataFromGraphQL = async (modules, apiConfig, isContextual, pageName) => {
  logger.debug(`modules: ${JSON.stringify(modules)}\n\napiConfig: ${JSON.stringify(apiConfig)}`);
  const queryData = await QueryBuilder.getQuery(modules, apiConfig);
  return executeGraphQLQuery({
    query: queryData.query,
    fetchPolicy: queryData.cacheType,
    cacheTag: queryData.cacheTag,
    isContextual,
    apiConfig,
    pageName,
  });
};

/**
 * Just fetches Queries based on passed module for logging, and it doesn't executes the query
 * @param {*} moduleName Module for which query needs to be executed
 * @returns {Promise} Resolves with data or rejects with error object
 */
export const fetchRawDataFromGraphQL = async (modules, apiConfig) => {
  return QueryBuilder.getRawQuery(modules, apiConfig);
};

/**
 * @summary Fetches Queries based on passed module, then executes the query and returns a promise for query execution
 * @param {Object} reqObj request param with endpoints and payload
 * @returns {Promise} Resolves with unbxd or stateful client based on request object or returns null
 */
export const executeStatefulAPICall = (reqObj, errHandler, newStateFullClient = false) => {
  if (!reqObj.webService) {
    return null;
  }
  const apiConfigObj = getAPIConfig();
  if (newStateFullClient) {
    return StatefulAPIClient(apiConfigObj, reqObj).catch(errHandler || errorHandler);
  }
  return StatefulAPIOldClient(apiConfigObj, reqObj).catch(errHandler || errorHandler);
};

export const executeUnbxdAPICall = (reqObj, originalURL, xappURLConfig, config) => {
  if (!reqObj.webService) {
    return null;
  }
  const apiConfigObj = config || getAPIConfig();
  const { isHplp } = xappURLConfig || {};
  if (isHplp) return UnbxdAPIClient(apiConfigObj, reqObj, originalURL, xappURLConfig);
  return UnbxdAPIClient(apiConfigObj, reqObj, originalURL, xappURLConfig).catch(errorHandler); // TODO - Make a new Instance and for GRAPHQL as well..
};

export const executeExternalAPICall = (reqObj) => {
  if (!reqObj.webService) {
    return null;
  }
  const apiConfigObj = getAPIConfig();
  return ExternalAPIClient(apiConfigObj, reqObj).catch((errObj) => {
    errorHandler(errObj, true);
  });
};

export default {
  fetchModuleDataFromGraphQL,
  fetchRawDataFromGraphQL,
  executeGraphQLQuery,
};
